import { Check, Lock, Info, Zap } from 'lucide-react';
import { resolveIcon } from '../icon-map';
import { money } from '../LiveBasket';

function BoostPackPill({ pack, selected, onSelect }) {
  return (
    <button
      type="button"
      data-testid={`boost-pack-${pack.id}`}
      onClick={(e) => { e.stopPropagation(); onSelect(pack); }}
      className={`relative text-left rounded-xl border p-3 transition-all ${
        selected
          ? 'border-[#00E5FF] bg-[#00E5FF]/15 shadow-[0_0_18px_rgba(0,229,255,0.25)]'
          : 'border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.02] hover:border-[#00E5FF]/40'
      }`}
    >
      {selected && (
        <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-[#00E5FF] flex items-center justify-center">
          <Check className="w-2.5 h-2.5 text-[#0A0A0A]" strokeWidth={3} />
        </span>
      )}
      <p className="font-['Outfit'] text-[11px] sm:text-xs font-bold text-[#0A0A0A] dark:text-white">{pack.name}</p>
      <p className="font-['Outfit'] text-base sm:text-lg font-black text-[#00E5FF] mt-0.5">
        {money(pack.price_monthly)}<span className="text-[10px] text-slate-500 dark:text-slate-500 font-normal">/mo</span>
      </p>
      <p className="text-[10px] text-slate-600 dark:text-slate-400 font-mono mt-1 truncate">{pack.credits}</p>
      <p className="text-[10px] text-slate-500 dark:text-slate-500 mt-0.5 truncate">{pack.interactions}</p>
    </button>
  );
}

/** Inline slider that appears under any selected boost-enabled bolt-on. */
function BoostSlider({ bolt, selectedPack, onSelect }) {
  const packs = bolt.boost_options || [];
  if (packs.length === 0) return null;
  const idx = Math.max(0, packs.findIndex(p => p.id === selectedPack?.id));
  const current = packs[idx] || packs[0];
  return (
    <div
      data-testid={`boost-slider-${bolt.id}`}
      className="mt-4 rounded-xl border border-[#00E5FF]/30 bg-gradient-to-br from-[#00E5FF]/[0.06] to-violet-500/[0.04] p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-[#00E5FF]" />
          <p className="text-[10px] uppercase tracking-widest text-[#0A0A0A] dark:text-white font-bold">AI Credit Pack</p>
        </div>
        <p className="font-['Outfit'] text-sm font-black text-[#00E5FF]">
          +{money(current.price_monthly)}<span className="text-[10px] text-slate-500 dark:text-slate-500 font-normal">/mo</span>
        </p>
      </div>

      {current.description && (
        <p className="text-[11px] text-slate-600 dark:text-slate-400 mb-3 leading-relaxed">{current.description}</p>
      )}

      {/* Native range slider for fluid feel */}
      <input
        type="range"
        data-testid={`boost-range-${bolt.id}`}
        min={0}
        max={packs.length - 1}
        step={1}
        value={idx}
        onChange={(e) => onSelect(packs[Number(e.target.value)])}
        className="w-full accent-[#00E5FF] cursor-pointer mb-3"
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        {packs.map(p => (
          <BoostPackPill key={p.id} pack={p} selected={p.id === current.id} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
}

function BoltOnCard({ bolt, selected, onToggle, selectedBoost, onSelectBoost, locked }) {
  const Icon = resolveIcon(bolt.icon);
  return (
    <button
      type="button"
      data-testid={`bolton-${bolt.id}`}
      onClick={() => onToggle(bolt)}
      disabled={locked}
      className={`group text-left relative rounded-2xl border p-4 sm:p-5 transition-all active:scale-[0.99] ${
        locked
          ? 'border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-white/[0.01] opacity-60 cursor-not-allowed'
          : selected
            ? 'border-[#00E5FF] bg-[#00E5FF]/10 shadow-[0_0_24px_rgba(0,229,255,0.15)]'
            : 'border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.02] hover:border-slate-300 dark:border-white/25 hover:bg-slate-100 dark:bg-white/[0.04]'
      }`}
    >
      {selected && !locked && (
        <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#00E5FF] flex items-center justify-center">
          <Check className="w-3.5 h-3.5 text-[#0A0A0A]" strokeWidth={3} />
        </span>
      )}
      {locked && (
        <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-slate-200 dark:bg-white/10 flex items-center justify-center">
          <Lock className="w-3 h-3 text-slate-500" />
        </span>
      )}
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 transition-colors
        ${selected && !locked ? 'bg-[#00E5FF] text-[#0A0A0A]' : 'bg-slate-100 dark:bg-white/5 text-[#00E5FF] group-hover:bg-[#00E5FF]/10'}`}>
        <Icon className="w-4 h-4" />
      </div>
      <h3 className="font-['Outfit'] text-sm sm:text-base font-bold text-[#0A0A0A] dark:text-white mb-1 pr-8">{bolt.name}</h3>
      <p className="text-[11px] sm:text-xs text-slate-400 dark:text-slate-400 leading-relaxed mb-3">{bolt.description}</p>
      <div className="flex items-center justify-between gap-2">
        <span className={`inline-block text-xs font-mono font-semibold ${selected && !locked ? 'text-[#00E5FF]' : 'text-slate-400 dark:text-slate-300'}`}>
          {money(bolt.price)}{bolt.price_suffix || ''}
        </span>
        {bolt.has_boost && (
          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-violet-500 dark:text-violet-300 bg-violet-500/10 border border-violet-500/30 px-1.5 py-0.5 rounded-md">
            <Zap className="w-2.5 h-2.5" /> Boost
          </span>
        )}
      </div>

      {/* Inline credit-pack slider */}
      {selected && bolt.has_boost && !locked && (
        <BoostSlider bolt={bolt} selectedPack={selectedBoost} onSelect={onSelectBoost} />
      )}
    </button>
  );
}

export default function StepBoltOns({
  boltOns,
  selectedIds,
  onToggle,
  boosts,
  onSelectBoost,
  hasCarePlanSelected,
}) {
  const standalone = boltOns.filter(b => !b.requires_care);
  const careRequired = boltOns.filter(b => b.requires_care);

  // Are any care-required bolt-ons currently selected?
  const careRequiredSelectedCount = careRequired.filter(b => selectedIds.includes(b.id)).length;
  const showCareWarning = careRequiredSelectedCount > 0 && !hasCarePlanSelected;

  return (
    <div data-testid="step-boltons" className="animate-[fadeIn_0.4s_ease]">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-widest text-[#00E5FF] mb-2 font-bold">Step 2 · Power-ups</p>
        <h2 className="font-['Outfit'] text-2xl sm:text-4xl font-bold text-[#0A0A0A] dark:text-white leading-tight">Supercharge your build</h2>
        <p className="text-sm text-slate-400 dark:text-slate-400 mt-2 max-w-md">Tap as many as you need — they fly straight into your basket. You can skip this step entirely.</p>
      </div>

      {/* ─── TOP — Standalone-safe ───────────────────────────────── */}
      {standalone.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-widest bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300/50 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400">
              Standalone safe
            </span>
            <p className="text-[11px] text-slate-500 dark:text-slate-500">Work without a Care Plan or our hosting.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {standalone.map(b => (
              <BoltOnCard
                key={b.id}
                bolt={b}
                selected={selectedIds.includes(b.id)}
                onToggle={onToggle}
                selectedBoost={boosts?.[b.id]}
                onSelectBoost={(pack) => onSelectBoost(b.id, pack)}
              />
            ))}
          </div>
        </section>
      )}

      {/* ─── BOTTOM — Care-Plan dependent ────────────────────────── */}
      {careRequired.length > 0 && (
        <section>
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-widest bg-amber-100 dark:bg-amber-500/10 border border-amber-300/50 dark:border-amber-500/30 text-amber-800 dark:text-amber-300">
                Requires Care Plan
              </span>
              <p className="text-[11px] text-slate-500 dark:text-slate-500">Tied to our hosting & admin panel.</p>
            </div>
          </div>

          {showCareWarning && (
            <div
              data-testid="bolton-care-warning"
              className="mb-3 rounded-xl border border-amber-300/60 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/[0.07] p-3 sm:p-4 flex items-start gap-3 animate-[fadeIn_0.25s_ease]"
            >
              <Info className="w-4 h-4 text-amber-700 dark:text-amber-300 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-semibold text-amber-900 dark:text-amber-200 leading-snug">
                  These power-ups need a Care Plan to keep working.
                </p>
                <p className="text-[11px] sm:text-xs text-amber-800/90 dark:text-amber-200/80 leading-relaxed mt-0.5">
                  They run on our hosting & admin infrastructure — without a Care Plan they'll stop working after handover. You'll be asked to pick one before you continue.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {careRequired.map(b => (
              <BoltOnCard
                key={b.id}
                bolt={b}
                selected={selectedIds.includes(b.id)}
                onToggle={onToggle}
                selectedBoost={boosts?.[b.id]}
                onSelectBoost={(pack) => onSelectBoost(b.id, pack)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
